import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useRequiredUser} from '@hconnect/login'
import {useUpmPlantContext} from '@hconnect/upm'
import {useCallback} from 'react'

import {PlannerTrackedFeatures} from '../enums'

const commonEvents = ['userMovesPage'] as const

const optimizerEvents = ['onViewOptimizedPlan', 'onAcceptOptimizedPlan'] as const

const productionPlanningEvents = [
  'onSubmitPlanClick',
  'onEditScheduleItem',
  'onDeleteScheduleItem',
  'onAddScheduleItem',
  'onCommentAddToScheduleItem',
  'onCommentEditToScheduleItem',
  'onCommentDeleteToScheduleItem',
  'onCommentAddToAsset',
  'onCommentDeleteToAsset',
  'onCommentEditToAsset',
  'onExportProductionPlan'
] as const

const rawMaterialsPlanningEvents = ['userEntersMaterialPlan'] as const

// TODO guided tours events in the scope of HCP-65350

type PlannerEventName = (
  | typeof commonEvents
  | typeof optimizerEvents
  | typeof productionPlanningEvents
  | typeof rawMaterialsPlanningEvents
)[number]

type PlannerEvent = {
  name: PlannerEventName
  [key: string]: unknown
}

const mapEventsToFeatures = (
  events: readonly PlannerEventName[],
  feature: PlannerTrackedFeatures
) =>
  events.reduce(
    (acc, event) => {
      acc[event] = feature
      return acc
    },
    {} as Record<PlannerEventName, PlannerTrackedFeatures>
  )

const eventToFeatureMap: Record<PlannerEvent['name'], PlannerTrackedFeatures> = {
  ...mapEventsToFeatures(optimizerEvents, PlannerTrackedFeatures.Optimizer),
  ...mapEventsToFeatures(productionPlanningEvents, PlannerTrackedFeatures.ProductionPlanning),
  ...mapEventsToFeatures(rawMaterialsPlanningEvents, PlannerTrackedFeatures.RawMaterialsPlanning)
}

export const useTrackPlannerEvent = () => {
  const {plantId} = useUpmPlantContext()
  const {country, name: plantName} = useUpmPlantContext()
  const {user_id: userId} = useRequiredUser()

  return useCallback(
    ({name, ...otherEventProps}: PlannerEvent) => {
      const eventFeature = eventToFeatureMap[name]

      return trackEvent(name, {
        product: 'planner',
        plantId,
        plantName,
        country,
        userId,
        ...(eventFeature ? {feature: eventFeature} : {}),
        ...otherEventProps
      })
    },
    [country, plantId, plantName, userId]
  )
}
